import { useState, useMemo } from 'react'
import { userPreferencesQueryOptions } from '~/queries/user'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'

export type SortType = 'lastViewed' | 'alphabetical' | 'dateCreated'

export type Bot = {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  deployedAt?: string
  integrations?: Record<string, { id: string }>
  alwaysAlive?: boolean
}

export const useBotSort = (bots: Bot[], defaultSortType: SortType = 'dateCreated', workspaceId: string) => {
  const [sortType, setSortType] = useState<SortType>(defaultSortType)

  const recentBots = useTanstackSuspenseQuery(
    userPreferencesQueryOptions({ path: '$workspaceId/botHistory', params: { workspaceId } })
  ).data.filter((b) => !!bots.find((bot) => bot.id === b))

  const sortedBots = useMemo(() => {
    if (sortType === 'lastViewed') {
      const viewedBots = recentBots
        .map((id) => bots.find((bot) => bot.id === id))
        .filter((bot) => bot !== undefined) as Bot[]

      const viewedBotIds = new Set(recentBots)
      const unviewedBots = bots.filter((bot) => !viewedBotIds.has(bot.id))

      const uniqueBots = new Map<string, Bot>()

      viewedBots.forEach((bot) => {
        if (!uniqueBots.has(bot.id)) {
          uniqueBots.set(bot.id, bot)
        }
      })

      unviewedBots.forEach((bot) => {
        if (!uniqueBots.has(bot.id)) {
          uniqueBots.set(bot.id, bot)
        }
      })

      return Array.from(uniqueBots.values())
    }

    return [...bots].sort((a, b) => {
      switch (sortType) {
        case 'alphabetical':
          return a.name.localeCompare(b.name)

        case 'dateCreated':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()

        default:
          return 0
      }
    })
  }, [bots, sortType, recentBots])

  return {
    sortType,
    setSortType,
    sortedBots,
  }
}
